"use client"

import Image from "next/image"
import { ArrowRight } from "lucide-react"
import { useRef } from "react"
import { motion, useInView } from "framer-motion"

import Ownership from "@/app/(landing)/img/collector_dialog.png"

import {
  textVariants,
  imageVariants,
  buttonVariants,
  standardScrollConfig,
} from "@/utils/animations"
import SectionHeader from "./SectionHeader"
import LoginButton from "@/app/components/LoginButton"

export default function CTA() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, standardScrollConfig)

  return (
    <section
      ref={sectionRef}
      className="bg-gradient-to-b from-ivory to-white py-0 lg:py-24 relative"
      aria-labelledby="cta-heading"
    >
      <div className="container mx-auto">
        <motion.div
          className="shadow-2xl max-w-7xl overflow-hidden inset   lg:h-[640px] bg-primary mt-16 lg:mt-0 rounded-xl  pt-16 lg:pl-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.8 }}
        >
          <div className="h-full flex flex-col lg:grid lg:grid-cols-2  lg:grid-rows-none gap-8 lg:gap-12">
            {/* Left Column - Content */}
            <motion.div
              className="flex flex-col gap-4 px-6 lg:px-0 lg:pb-16 justify-center items-center lg:items-start text-center lg:text-left lg:gap-8 flex-1 lg:max-w-full max-w-xl mx-auto "
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.3,
                  },
                },
              }}
            >
              {/* Section Header */}
              <div className="flex flex-col gap-4 lg:gap-5">
                {/* Main Title */}
                <SectionHeader
                  className="text-center lg:text-left text-white mx-0"
                  ref={sectionRef}
                >
                  Ignite your next idea
                </SectionHeader>
              </div>
              {/* CTA Content */}
              <div className="flex flex-col gap-10 items-center lg:items-start">
                {/* Section Description */}
                <motion.p
                  className="text-primary-foreground/80 text-base sm:text-lg "
                  variants={textVariants}
                >
                  We're building a future where the internet's best ideas thrive
                  through a value system that rewards quality, not clickbait.
                  <br />
                  <br />
                  No paywalls. No subscription treadmills. Just great ideas that
                  earn what they're worth.
                </motion.p>
                {/* CTA Button */}
                <motion.div variants={buttonVariants} whileHover="hover">
                  <LoginButton
                    className="bg-primary-foreground text-primary hover:bg-primary-foreground/50"
                    aria-label="Get started"
                    rightIcon={<ArrowRight className="ml-2 h-4 w-4" />}
                    redirectNewUserToOnboarding
                  />
                </motion.div>
              </div>
            </motion.div>
            {/* Right Column - Image */}
            <motion.div
              className=" flex  w-full xs:w-2/3 lg:w-full lg:h-full pl-6 lg:pl-0 pr-6 justify-end items-end mx-auto "
              variants={imageVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              <Image
                src={Ownership}
                alt="CTA section image"
                className="rounded-tl-lg w-full h-auto justify-end self-end"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
